import React from 'react';
import { FormItem } from 'azure-devops-ui/FormItem';
import { TextField, TextFieldStyle, TextFieldWidth } from 'azure-devops-ui/TextField';
import { Header, TitleSize } from 'azure-devops-ui/Header';
import { IHeaderCommandBarItem } from 'azure-devops-ui/HeaderCommandBar';
import * as _ from 'lodash';
import Action, { IActionParentProps } from '../Action';
import useStreamingSession from '../../hooks/useSession';
import { OggFieldService } from '../../services/ogg.service';
import { Suggestion } from '../../types';
import { useSettings } from '../../hooks/useSettings';
import MarkdownContent, { markdownToHtml } from '../MarkdownContent';

interface ISuggestionContentProps {
  streamingContent: string;
  oggService: OggFieldService;
}

function WriterContent(props: ISuggestionContentProps) {
  const settings = useSettings();

  const commandBarItems: IHeaderCommandBarItem[] = settings.displayApplySuggestionsButton
    ? [
        {
          iconProps: { iconName: 'Save' },
          id: 'applySuggestions',
          isPrimary: true,
          text: 'Apply suggestions',
          onActivate: () => {
            applySuggestions();
          },
        },
      ]
    : [];

  async function applySuggestions() {
    const htmlSuggestion: Suggestion = {
      workItemTitle: suggestion.workItemTitle,
      workItemDescription: await markdownToHtml(suggestion.workItemDescription),
      workItemAcceptanceCriteria: await markdownToHtml(suggestion.workItemAcceptanceCriteria),
    };

    props.oggService.applySuggestions(htmlSuggestion);
  }

  function toSuggestionResponse(message: string): Suggestion {
    const obj = JSON.parse(message);
    return _.mapKeys(obj, (value, key) => _.camelCase(key));
  }

  function trySuffixes(suffixes: string[]) {
    for (let suffix of suffixes) {
      try {
        return toSuggestionResponse(props.streamingContent + suffix);
      } catch (error) {}
    }

    console.warn(
      "Couldn't create a suggestion response after applying suffixes. Here's the input: ",
      `'${props.streamingContent}'`
    );
    return {};
  }

  const suggestion: Suggestion = trySuffixes(['', '"}', '""}', '\\""}']);
  console.debug('loading suggestion');

  return (
    <div className='suggestionContainer'>
      <Header
        title={''}
        titleSize={TitleSize.Medium}
        commandBarItems={commandBarItems}
        titleAriaLevel={3}
      />
      <FormItem label='Title'>
        <TextField
          prefixIconProps={{
            iconName: 'WorkItem',
          }}
          value={suggestion.workItemTitle}
          width={TextFieldWidth.auto}
          style={TextFieldStyle.inline}
        />
      </FormItem>
      <div className='spacer' />
      <FormItem label='Description'>
        <div className='large-text-box ado-border description'>
          <MarkdownContent content={suggestion.workItemDescription} />
        </div>
      </FormItem>
      <div className='spacer' />
      <FormItem label='Acceptance Criteria'>
        <div className='large-text-box ado-border description'>
          <MarkdownContent content={suggestion.workItemAcceptanceCriteria} />
        </div>
      </FormItem>
    </div>
  );
}

export function Writer({ streamingSession, oggService }: IActionParentProps) {
  console.debug('loading writer');
  const [streamingContent] = useStreamingSession(streamingSession);

  return (
    <Action
      presentation={{
        name: 'The Writer',
        description: (
          <p>
            I'll help you to write clear, actionable user stories and detailed acceptance criteria.
          </p>
        ),
        imageName: 'TheWriter.png',
        onStart: () => oggService.makeSuggestionStream(streamingSession),
      }}
      streamOutput={{
        loadingTitle: 'Ogg is enriching the work item...',
        loadingSubtitle: 'Fetching the suggestions from the server',
        render: (message) =>
          WriterContent({
            streamingContent: message,
            oggService: oggService,
          }),
        streamingContent,
        streamingSession,
      }}
    ></Action>
  );
}
